import { z } from 'zod';
import { OptionalTextSearchParam, PositiveIntegerSearchParam, searchValidator } from '../../lib/search-params.js';

/**
 * `/mail` search params. Imported by router.tsx (entry chunk): zod and the
 * search-param helpers only.
 *
 * - `accountId` — the mailbox (mail_accounts.id). Absent = the user's own
 *   primary mailbox, so older links keep their meaning. Never the mailbox's
 *   email address or owner name. A delegated mailbox stays read-only (Rule 14):
 *   the id only selects which mailbox to read, the API decides access.
 * - `folder` — a well-known folder name or a Graph folder id (opaque).
 * - `messageId` — the local mail_messages.id of the open message. Never a
 *   Graph message id or a synthetic negative id.
 *
 * Never in this URL: the search box, filter pills or labels, compose/draft
 * state, recipients, subjects, attachment ids.
 */

/** Graph folder ids run to ~150 characters; anything far longer is not a folder id. */
export const MAIL_FOLDER_MAX_LENGTH = 512;

export const MailSearchSchema = z.object({
  accountId: PositiveIntegerSearchParam,
  folder: OptionalTextSearchParam(MAIL_FOLDER_MAX_LENGTH),
  messageId: PositiveIntegerSearchParam,
});

/** `validateSearch` for `/mail`. Never throws; unknown keys are stripped. */
export const mailSearch = searchValidator(MailSearchSchema);
export type MailSearch = ReturnType<typeof mailSearch>;
